import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CheckCircle2, Volume2, VolumeX } from 'lucide-react';
import SIAOrb from '../shared/SIAOrb';

// Module-level audio controller to prevent overlap across React re-renders
let globalAudioController = {
  currentAudio: null,
  isProcessing: false,
  processedSteps: new Set(),
  sessionId: null,  // Track the current session
  audioLock: false  // Mutex for audio playback
};

// SIA's Educational Script - Step by Step
// Note: Text displays "SIA" but TTS pronounces it as "Siya"
const SIA_SCRIPT = [
  {
    id: 'intro',
    text: "Hi! I am SIA. I am here to help you understand the TDS process flow. First, let's understand TDS - Tax Deducted at Source. It's the tax collected right at the point of income.",
    showRail: false,
    activePod: -1,
    podStatus: null,
    showWaveform: true,
  },
  {
    id: 'rule',
    text: "But collecting isn't enough. As per the law, this tax must be deposited with the Government every month. Let's see the cycle.",
    showRail: false,
    activePod: -1,
    podStatus: null,
    showWaveform: true,
  },
  {
    id: 'april-show',
    text: "Here comes April - the first month of Quarter 1. Watch the timeline...",
    showRail: true,
    activePod: 0,
    podStatus: 'scanning',
  },
  {
    id: 'april-validate',
    text: "For April, the TDS must be deposited by 7th May. Processing deposit...",
    showRail: true,
    activePod: 0,
    podStatus: 'processing',
  },
  {
    id: 'april-success',
    text: "See? It's done! Our April compliance is complete. Now, let's talk about the MAY month.",
    showRail: true,
    activePod: 0,
    podStatus: 'complete',
  },
  {
    id: 'may-show',
    text: "May follows the same pattern. TDS deducted in May must be deposited by 7th June.",
    showRail: true,
    activePod: 1,
    podStatus: 'scanning',
  },
  {
    id: 'may-validate',
    text: "Processing May's TDS deposit...",
    showRail: true,
    activePod: 1,
    podStatus: 'processing',
  },
  {
    id: 'may-success',
    text: "Excellent! May compliance is complete. One more month to go for Quarter 1.",
    showRail: true,
    activePod: 1,
    podStatus: 'complete',
  },
  {
    id: 'june-show',
    text: "June is the final month of Quarter 1. TDS must be deposited by 7th July.",
    showRail: true,
    activePod: 2,
    podStatus: 'scanning',
  },
  {
    id: 'june-validate',
    text: "Processing June's deposit... Almost there!",
    showRail: true,
    activePod: 2,
    podStatus: 'processing',
  },
  {
    id: 'june-success',
    text: "All three months done! Now comes the important part - filing the TDS Return.",
    showRail: true,
    activePod: 2,
    podStatus: 'complete',
  },
  {
    id: 'deadline-info',
    text: "Remember: 31st July is the due date for Quarter 1 TDS Return filing. For other months, deposit is due by 7th of the next month. Except March - its deposit is due by 30th April!",
    showRail: true,
    activePod: 2,
    podStatus: 'complete',
    showDeadline: true,
  },
  {
    id: 'merge',
    text: "Now watch as all three months merge into Form 24Q - your quarterly TDS Return!",
    showRail: true,
    activePod: 2,
    podStatus: 'complete',
    isMerging: true,
    showWaveform: true,
  },
  {
    id: 'complete',
    text: "Your Form 24Q is ready! Let's start filing...",
    showRail: false,
    activePod: -1,
    podStatus: null,
    showCore: true,
    showWaveform: true,
  },
];

// Month pods data
const MONTHS = [
  { id: 'apr', label: 'APRIL', dueDate: '7th MAY' },
  { id: 'may', label: 'MAY', dueDate: '7th JUNE' },
  { id: 'jun', label: 'JUNE', dueDate: '7th JULY' },
];

// Typewriter Text Component
const TypewriterText = ({ text, isActive, speed = 50 }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    if (!isActive) {
      setDisplayedText('');
      setCurrentIndex(0);
      return;
    }
    
    if (currentIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayedText(text.slice(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      }, speed);
      return () => clearTimeout(timer);
    }
  }, [text, isActive, currentIndex, speed]);
  
  // Reset when text changes
  useEffect(() => {
    setDisplayedText('');
    setCurrentIndex(0);
  }, [text]);
  
  return (
    <span>
      {displayedText}
      {isActive && currentIndex < text.length && (
        <span className="inline-block w-0.5 h-5 bg-cyan-400 ml-1 animate-pulse" />
      )}
    </span>
  );
};

// Main Timeline Component
export default function TDSEducationalTimeline({ onComplete, onSkip }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [completedPods, setCompletedPods] = useState([]);
  const [typewriterSpeed, setTypewriterSpeed] = useState(50);
  
  const containerRef = useRef(null);
  const mountedRef = useRef(true);
  // Create a unique session ID for this timeline instance
  const sessionIdRef = useRef(Date.now().toString() + Math.random().toString(36));
  // Store speakText in a ref to avoid dependency issues
  const speakTextRef = useRef(null);
  
  const currentScript = SIA_SCRIPT[currentStep];
  
  // Initialize session on first mount only
  useEffect(() => {
    const mySessionId = sessionIdRef.current;
    
    // Only reset if this is a new session
    if (globalAudioController.sessionId !== mySessionId) {
      console.log('[Timeline] New session:', mySessionId);
      // Stop any existing audio
      if (globalAudioController.currentAudio) {
        globalAudioController.currentAudio.pause();
        globalAudioController.currentAudio = null;
      }
      // Reset controller for new session
      globalAudioController.processedSteps = new Set();
      globalAudioController.isProcessing = false;
      globalAudioController.sessionId = mySessionId;
    }
    
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      // Only stop audio if this session is still the active one
      if (globalAudioController.sessionId === mySessionId && globalAudioController.currentAudio) {
        globalAudioController.currentAudio.pause();
        globalAudioController.currentAudio = null;
      }
    };
  }, []);
  
  // Stop current audio
  const stopAudio = useCallback(() => {
    if (globalAudioController.currentAudio) {
      globalAudioController.currentAudio.pause();
      globalAudioController.currentAudio.currentTime = 0;
      globalAudioController.currentAudio = null;
    }
    setIsSpeaking(false);
  }, []);
  
  // Speak with TTS - with mutex lock to prevent overlapping audio
  const speakText = useCallback(async (text) => {
    // Acquire audio lock
    if (globalAudioController.audioLock) {
      console.log('[Audio] Skipping - audio lock held');
      return Promise.resolve(0);
    }
    globalAudioController.audioLock = true;
    console.log('[Audio] Lock acquired, playing:', text.substring(0, 30) + '...');
    
    // Always stop any existing audio first
    stopAudio();
    
    // Transform text for TTS pronunciation: "SIA" -> "Siya" (for Hindi-style pronunciation)
    const ttsText = text.replace(/\bSIA\b/g, 'Siya');
    
    try {
      if (!soundEnabled) {
        // If sound is off, just animate typewriter at default speed
        setIsSpeaking(true);
        setTypewriterSpeed(40);
        const estimatedDuration = text.length * 40 + 500;
        return new Promise(resolve => {
          setTimeout(() => {
            if (mountedRef.current) setIsSpeaking(false);
            globalAudioController.audioLock = false;
            console.log('[Audio] Lock released (sound off)');
            resolve(estimatedDuration);
          }, estimatedDuration);
        });
      }
      
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/tds/sia-speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: ttsText, voice: 'nova' })  // Use transformed text for TTS
      });
      
      const data = await response.json();
      
      if (data.success && data.audio_base64) {
        return new Promise((resolve) => {
          const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
          globalAudioController.currentAudio = audio;
          
          // Get duration once loaded
          audio.addEventListener('loadedmetadata', () => {
            const duration = audio.duration * 1000;
            const charSpeed = Math.max(20, Math.min(80, (duration - 500) / text.length));
            if (mountedRef.current) setTypewriterSpeed(charSpeed);
          });
          
          if (mountedRef.current) setIsSpeaking(true);
          
          audio.onended = () => {
            if (mountedRef.current) setIsSpeaking(false);
            globalAudioController.currentAudio = null;
            globalAudioController.audioLock = false;
            console.log('[Audio] Lock released (audio ended)');
            resolve(audio.duration * 1000 || text.length * 50 + 1000);
          };
          
          audio.onerror = () => {
            if (mountedRef.current) setIsSpeaking(false);
            globalAudioController.currentAudio = null;
            globalAudioController.audioLock = false;
            console.log('[Audio] Lock released (audio error)');
            resolve(text.length * 50 + 500);
          };
          
          audio.play().catch(() => {
            if (mountedRef.current) setIsSpeaking(false);
            globalAudioController.audioLock = false;
            console.log('[Audio] Lock released (play error)');
            resolve(text.length * 50 + 500);
          });
        });
      }
    } catch (err) {
      console.log('TTS error:', err);
    }
    
    // Fallback
    if (mountedRef.current) setIsSpeaking(true);
    const fallbackDuration = text.length * 50 + 500;
    return new Promise(resolve => {
      setTimeout(() => {
        if (mountedRef.current) setIsSpeaking(false);
        globalAudioController.audioLock = false;
        console.log('[Audio] Lock released (fallback)');
        resolve(fallbackDuration);
      }, fallbackDuration);
    });
  }, [soundEnabled, stopAudio]);
  
  // Keep speakTextRef up to date
  useEffect(() => {
    speakTextRef.current = speakText;
  }, [speakText]);
  
  // Process current step - runs only once per step using global controller
  useEffect(() => {
    const mySessionId = sessionIdRef.current;
    
    // Only process if this is the active session
    if (globalAudioController.sessionId !== mySessionId) {
      console.log('[Timeline] Skipping - different session');
      return;
    }
    
    // Use global controller to prevent duplicate processing
    if (globalAudioController.isProcessing || globalAudioController.processedSteps.has(currentStep)) {
      console.log('[Timeline] Skipping step', currentStep, '- already processed or processing');
      return;
    }
    
    if (currentStep >= SIA_SCRIPT.length) {
      onComplete?.();
      return;
    }
    
    console.log('[Timeline] Processing step', currentStep);
    
    // Mark as processing immediately using global state
    globalAudioController.isProcessing = true;
    globalAudioController.processedSteps.add(currentStep);
    
    const runStep = async () => {
      const script = SIA_SCRIPT[currentStep];
      
      // Update completed pods
      if (script.podStatus === 'complete' && script.activePod >= 0) {
        setCompletedPods(prev => {
          if (!prev.includes(script.activePod)) {
            return [...prev, script.activePod];
          }
          return prev;
        });
      }
      
      // Speak and wait for completion - use ref to get latest speakText
      if (speakTextRef.current) {
        await speakTextRef.current(script.text);
      }
      
      // Check if still mounted and correct session before continuing
      if (!mountedRef.current || globalAudioController.sessionId !== mySessionId) {
        globalAudioController.isProcessing = false;
        return;
      }
      
      // Small pause between steps
      await new Promise(r => setTimeout(r, 800));
      
      // Check again if still mounted and correct session
      if (!mountedRef.current || globalAudioController.sessionId !== mySessionId) {
        globalAudioController.isProcessing = false;
        return;
      }
      
      // Reset processing flag
      globalAudioController.isProcessing = false;
      
      // Move to next step or complete
      if (currentStep === SIA_SCRIPT.length - 1) {
        setTimeout(() => {
          if (mountedRef.current && globalAudioController.sessionId === mySessionId) {
            onComplete?.();
          }
        }, 1500);
      } else {
        setCurrentStep(prev => prev + 1);
      }
    };
    
    runStep();
  }, [currentStep, onComplete]);  // eslint-disable-line react-hooks/exhaustive-deps
  // Note: speakTextRef is intentionally not in deps to prevent retriggering
  
  // Get pod status
  const getPodStatus = (index) => {
    if (completedPods.includes(index)) return 'complete';
    if (currentScript?.activePod === index) return currentScript.podStatus;
    return 'inactive';
  };
  
  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 bg-[#050510] flex flex-col items-center justify-center overflow-hidden"
      style={{ height: '100vh', maxHeight: '100vh' }}
    >
      {/* Background Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(139,92,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(139,92,246,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />
      
      {/* Ambient Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-purple-500/10 rounded-full blur-[100px]" />
      
      {/* Voice Waveform Visualizer - Center of screen (shows when no rail OR showWaveform is true) */}
      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center gap-[3px] transition-opacity duration-500 ${
        currentScript?.showWaveform || !currentScript?.showRail ? 'opacity-100' : 'opacity-0'
      }`}>
        {/* Waveform bars - using CSS animation with varied delays */}
        {[...Array(32)].map((_, i) => (
          <div
            key={i}
            className={`waveform-bar rounded-full ${
              isSpeaking 
                ? 'bg-gradient-to-t from-purple-600 via-cyan-400 to-purple-400 speaking' 
                : 'bg-purple-500/40 idle'
            }`}
            style={{
              '--delay': `${i * 0.05}s`,
              '--index': i,
            }}
          />
        ))}
      </div>
      
      {/* SIA Label above waveform */}
      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[100px] transition-opacity duration-500 ${
        currentScript?.showWaveform || !currentScript?.showRail ? 'opacity-100' : 'opacity-0'
      }`}>
        <div className="flex items-center gap-3">
          <div className={`w-2 h-2 rounded-full ${isSpeaking ? 'bg-cyan-400 animate-pulse shadow-[0_0_10px_rgba(34,211,238,0.8)]' : 'bg-purple-500/50'}`} />
          <span className={`text-sm font-medium tracking-widest transition-colors duration-300 ${isSpeaking ? 'text-cyan-300' : 'text-purple-400/60'}`}>
            {isSpeaking ? 'SIA SPEAKING' : 'SIA'}
          </span>
          <div className={`w-2 h-2 rounded-full ${isSpeaking ? 'bg-cyan-400 animate-pulse shadow-[0_0_10px_rgba(34,211,238,0.8)]' : 'bg-purple-500/50'}`} />
        </div>
      </div>
      
      {/* TaxSetu Logo - Top Left */}
      <div className="absolute top-4 left-6 z-50">
        <img 
          src="https://customer-assets.emergentagent.com/job_61ca5ff4-bcc2-4cdc-8453-7407936ace18/artifacts/6nklllpo_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png"
          alt="TaxSetu AI"
          className="h-12 w-auto"
        />
      </div>
      
      {/* Header - Center */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 text-center z-50">
        <h1 className="text-2xl font-bold text-white mb-1">
          TDS <span className="text-purple-400">Process Flow</span>
        </h1>
        <p className="text-purple-300/60 text-sm">Quarter 1 - Financial Year 2025-26</p>
      </div>
      
      {/* Sound Toggle */}
      <button
        onClick={() => {
          setSoundEnabled(!soundEnabled);
          if (soundEnabled) stopAudio();
        }}
        className={`absolute top-4 right-6 z-50 p-3 rounded-xl border transition-all ${
          soundEnabled 
            ? 'bg-cyan-500/20 border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/30' 
            : 'bg-white/5 border-white/10 text-gray-500 hover:bg-white/10'
        }`}
        data-testid="sound-toggle"
      >
        {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
      </button>
      
      {/* Skip Button */}
      <button
        onClick={() => {
          stopAudio();
          onSkip?.(); // Call onSkip instead of onComplete
        }}
        className="absolute top-5 right-20 z-50 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10 transition-all text-sm"
        data-testid="skip-btn"
      >
        Skip →
      </button>
      
      {/* Main Content Area */}
      <div className="relative flex flex-col items-center justify-center w-full max-w-5xl px-8 mt-16">
        
        {/* Timeline Rail & Pods */}
        <div className={`relative w-full transition-all duration-1000 ${
          currentScript?.showRail ? 'opacity-100' : 'opacity-0'
        }`}>
          {/* Holographic Rail */}
          <div className="relative h-2 bg-gradient-to-r from-transparent via-purple-500/30 to-transparent rounded-full">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-400/50 to-transparent blur-sm" />
            
            {/* Scanner Light */}
            {currentScript?.podStatus === 'scanning' && (
              <div 
                className="absolute top-1/2 -translate-y-1/2 w-32 h-8 bg-gradient-to-r from-transparent via-cyan-400 to-transparent blur-md animate-pulse"
                style={{ 
                  left: `${(currentScript.activePod + 0.5) * 33}%`,
                  transform: 'translateX(-50%) translateY(-50%)'
                }}
              />
            )}
          </div>
          
          {/* Month Pods */}
          <div className="flex justify-center gap-12 mt-8">
            {MONTHS.map((month, index) => {
              const status = getPodStatus(index);
              const isActive = currentScript?.activePod === index;
              const isVisible = currentScript?.showRail && (completedPods.includes(index) || currentScript.activePod >= index);
              
              return (
                <div 
                  key={month.id}
                  className={`relative transition-all duration-700 ${
                    currentScript?.isMerging 
                      ? 'scale-0 opacity-0' 
                      : isVisible 
                        ? 'scale-100 opacity-100' 
                        : 'scale-75 opacity-0'
                  }`}
                  style={{ 
                    transitionDelay: currentScript?.isMerging ? `${index * 100}ms` : `${index * 200}ms`,
                    transform: currentScript?.isMerging ? `translateX(${(1 - index) * 150}px) scale(0)` : undefined
                  }}
                >
                  {/* Pod Glow */}
                  <div className={`absolute -inset-4 rounded-2xl transition-all duration-500 ${
                    status === 'scanning' ? 'bg-cyan-500/20 animate-pulse' :
                    status === 'processing' ? 'bg-yellow-500/20 animate-pulse' :
                    status === 'complete' ? 'bg-green-500/10' : ''
                  }`} />
                  
                  {/* Glass Pod */}
                  <div className={`relative w-32 h-40 rounded-xl backdrop-blur-md border-2 transition-all duration-500 ${
                    status === 'complete' 
                      ? 'bg-green-900/30 border-green-400/50 shadow-[0_0_30px_rgba(34,197,94,0.3)]' 
                      : status === 'processing'
                        ? 'bg-yellow-900/30 border-yellow-400/50 shadow-[0_0_30px_rgba(234,179,8,0.3)]'
                        : status === 'scanning'
                          ? 'bg-cyan-900/30 border-cyan-400/50 shadow-[0_0_30px_rgba(34,211,238,0.3)]' 
                          : 'bg-purple-900/20 border-purple-500/30'
                  }`}>
                    
                    {/* Status Icon */}
                    <div className={`absolute -top-3 -right-3 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 ${
                      status === 'complete' ? 'bg-green-500 scale-100' : 
                      status === 'processing' ? 'bg-yellow-500 scale-100 animate-spin' :
                      'bg-gray-600 scale-75'
                    }`}>
                      {status === 'complete' ? (
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      ) : status === 'processing' ? (
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                      ) : (
                        <div className="w-2 h-2 bg-gray-400 rounded-full" />
                      )}
                    </div>
                    
                    {/* Month Label */}
                    <div className="flex flex-col items-center justify-center h-full p-3">
                      <span className={`text-xl font-bold transition-colors duration-300 ${
                        status !== 'inactive' ? 'text-white' : 'text-purple-300/50'
                      }`}>
                        {month.label}
                      </span>
                      
                      {/* Due Date with Neon Scanner Effect */}
                      <div className={`mt-3 px-3 py-1.5 rounded-md text-xs font-mono transition-all duration-500 ${
                        status === 'scanning' 
                          ? 'bg-cyan-500/40 text-cyan-200 shadow-[0_0_15px_rgba(34,211,238,0.5)] animate-pulse' 
                          : status === 'processing'
                            ? 'bg-yellow-500/30 text-yellow-200'
                            : status === 'complete' 
                              ? 'bg-green-500/20 text-green-300' 
                              : 'bg-purple-500/20 text-purple-300/50'
                      }`}>
                        {month.dueDate}
                      </div>
                      
                      <span className="text-[10px] text-gray-400 mt-2">TDS Deposit Due</span>
                    </div>
                    
                    {/* Scanner Beam */}
                    {status === 'scanning' && (
                      <div className="absolute inset-0 overflow-hidden rounded-xl">
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent animate-scan-down" />
                      </div>
                    )}
                  </div>
                  
                  {/* Connection Line to Rail */}
                  <div className={`absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full w-0.5 h-8 transition-all duration-500 ${
                    status !== 'inactive' ? 'bg-gradient-to-b from-purple-500/50 to-cyan-400' : 'bg-purple-500/20'
                  }`} />
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Deadline Alert */}
        {currentScript?.showDeadline && (
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 z-40 animate-bounce-slow">
            <div className="relative">
              <div className="absolute inset-0 rounded-2xl bg-red-500/20 animate-ping" style={{ animationDuration: '1.5s' }} />
              <div className="relative px-8 py-4 rounded-2xl bg-gradient-to-br from-red-900/80 to-orange-900/80 border-2 border-red-500/50 backdrop-blur-md shadow-[0_0_40px_rgba(239,68,68,0.4)]">
                <p className="text-red-300 text-sm font-medium mb-1">Q1 RETURN DEADLINE</p>
                <p className="text-2xl font-bold text-white">31st JULY</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Master Core - Form 24Q */}
        {currentScript?.showCore && (
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 z-40">
            <div className="relative animate-scale-in">
              {/* Energy Rings */}
              <div className="absolute -inset-8 rounded-full border-4 border-green-400/30 animate-spin-slow" />
              <div className="absolute -inset-4 rounded-full border-2 border-cyan-400/50 animate-reverse-spin" />
              
              {/* Core */}
              <div className="relative w-36 h-36 rounded-full bg-gradient-to-br from-[#1a0a3a] via-[#0a1a3a] to-[#0a0a2a] border-4 border-green-400 shadow-[0_0_60px_rgba(34,197,94,0.5)] flex items-center justify-center">
                <div className="absolute inset-4 rounded-full bg-green-500/20 blur-md" />
                <div className="relative text-center z-10">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-1 text-green-400" />
                  <span className="text-white font-bold text-sm">Form 24Q</span>
                  <p className="text-green-300/60 text-xs">Ready!</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* SIA Orb - Bottom Right */}
      <div className="absolute bottom-8 right-8 z-50">
        <SIAOrb isSpeaking={isSpeaking} size="large" />
      </div>
      
      {/* SIA Dialogue Box - Bottom Center */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-full max-w-2xl px-8 z-40">
        <div className={`bg-gradient-to-r from-purple-900/60 via-blue-900/60 to-purple-900/60 border border-purple-500/30 rounded-2xl px-6 py-4 backdrop-blur-md shadow-lg transition-all duration-500 ${
          currentScript ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <div className="flex items-start gap-4">
            <SIAOrb isSpeaking={isSpeaking} size="small" />
            <p className="text-gray-100 text-sm leading-relaxed flex-1 min-h-[60px]">
              <TypewriterText 
                text={currentScript?.text || ''} 
                isActive={true}
                speed={typewriterSpeed}
              />
            </p>
          </div>
        </div>
      </div>
      
      {/* Progress Indicator */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-50">
        {SIA_SCRIPT.map((_, index) => (
          <div 
            key={index}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentStep 
                ? 'bg-cyan-400 scale-125' 
                : index < currentStep 
                  ? 'bg-purple-400' 
                  : 'bg-gray-600'
            }`}
          />
        ))}
      </div>
      
      {/* CSS Animations */}
      <style>{`
        @keyframes scan-down {
          0% { transform: translateY(-100%); opacity: 0; }
          50% { opacity: 1; }
          100% { transform: translateY(4000%); opacity: 0; }
        }
        .animate-scan-down {
          animation: scan-down 1.5s ease-in-out infinite;
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
        @keyframes reverse-spin {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }
        .animate-reverse-spin {
          animation: reverse-spin 6s linear infinite;
        }
        @keyframes scale-in {
          0% { transform: scale(0); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-scale-in {
          animation: scale-in 0.8s ease-out forwards;
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(-10px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
        
        /* Waveform bars */
        .waveform-bar {
          width: 3px;
          transition: all 0.3s ease;
        }
        .waveform-bar.idle {
          height: 6px;
          animation: idle-pulse 2s ease-in-out infinite;
          animation-delay: var(--delay);
        }
        .waveform-bar.speaking {
          animation: waveform-active 0.4s ease-in-out infinite alternate;
          animation-delay: var(--delay);
          box-shadow: 0 0 8px rgba(34, 211, 238, 0.5), 0 0 16px rgba(139, 92, 246, 0.3);
        }
        @keyframes idle-pulse {
          0%, 100% { 
            height: 6px; 
            opacity: 0.4;
          }
          50% { 
            height: 12px; 
            opacity: 0.6;
          }
        }
        @keyframes waveform-active {
          0% { 
            height: 10px;
          }
          100% { 
            height: calc(20px + sin(var(--index) * 0.5) * 50px + 40px);
          }
        }
        /* Create varied heights using nth-child */
        .waveform-bar.speaking:nth-child(1) { animation-duration: 0.3s; }
        .waveform-bar.speaking:nth-child(2) { animation-duration: 0.35s; }
        .waveform-bar.speaking:nth-child(3) { animation-duration: 0.4s; }
        .waveform-bar.speaking:nth-child(4) { animation-duration: 0.45s; }
        .waveform-bar.speaking:nth-child(5) { animation-duration: 0.5s; }
        .waveform-bar.speaking:nth-child(6) { animation-duration: 0.55s; }
        .waveform-bar.speaking:nth-child(7) { animation-duration: 0.5s; }
        .waveform-bar.speaking:nth-child(8) { animation-duration: 0.45s; }
        .waveform-bar.speaking:nth-child(9) { animation-duration: 0.4s; }
        .waveform-bar.speaking:nth-child(10) { animation-duration: 0.35s; }
        .waveform-bar.speaking:nth-child(11) { animation-duration: 0.3s; }
        .waveform-bar.speaking:nth-child(12) { animation-duration: 0.35s; }
        .waveform-bar.speaking:nth-child(13) { animation-duration: 0.4s; }
        .waveform-bar.speaking:nth-child(14) { animation-duration: 0.45s; }
        .waveform-bar.speaking:nth-child(15) { animation-duration: 0.5s; }
        .waveform-bar.speaking:nth-child(16) { animation-duration: 0.55s; }
        .waveform-bar.speaking:nth-child(17) { animation-duration: 0.5s; }
        .waveform-bar.speaking:nth-child(18) { animation-duration: 0.45s; }
        .waveform-bar.speaking:nth-child(19) { animation-duration: 0.4s; }
        .waveform-bar.speaking:nth-child(20) { animation-duration: 0.35s; }
        .waveform-bar.speaking:nth-child(21) { animation-duration: 0.3s; }
        .waveform-bar.speaking:nth-child(22) { animation-duration: 0.35s; }
        .waveform-bar.speaking:nth-child(23) { animation-duration: 0.4s; }
        .waveform-bar.speaking:nth-child(24) { animation-duration: 0.45s; }
        .waveform-bar.speaking:nth-child(25) { animation-duration: 0.5s; }
        .waveform-bar.speaking:nth-child(26) { animation-duration: 0.55s; }
        .waveform-bar.speaking:nth-child(27) { animation-duration: 0.5s; }
        .waveform-bar.speaking:nth-child(28) { animation-duration: 0.45s; }
        .waveform-bar.speaking:nth-child(29) { animation-duration: 0.4s; }
        .waveform-bar.speaking:nth-child(30) { animation-duration: 0.35s; }
        .waveform-bar.speaking:nth-child(31) { animation-duration: 0.3s; }
        .waveform-bar.speaking:nth-child(32) { animation-duration: 0.35s; }
        
        /* Height variations for speaking */
        .waveform-bar.speaking:nth-child(1), .waveform-bar.speaking:nth-child(32) { --max-height: 25px; }
        .waveform-bar.speaking:nth-child(2), .waveform-bar.speaking:nth-child(31) { --max-height: 35px; }
        .waveform-bar.speaking:nth-child(3), .waveform-bar.speaking:nth-child(30) { --max-height: 45px; }
        .waveform-bar.speaking:nth-child(4), .waveform-bar.speaking:nth-child(29) { --max-height: 55px; }
        .waveform-bar.speaking:nth-child(5), .waveform-bar.speaking:nth-child(28) { --max-height: 60px; }
        .waveform-bar.speaking:nth-child(6), .waveform-bar.speaking:nth-child(27) { --max-height: 65px; }
        .waveform-bar.speaking:nth-child(7), .waveform-bar.speaking:nth-child(26) { --max-height: 70px; }
        .waveform-bar.speaking:nth-child(8), .waveform-bar.speaking:nth-child(25) { --max-height: 72px; }
        .waveform-bar.speaking:nth-child(9), .waveform-bar.speaking:nth-child(24) { --max-height: 74px; }
        .waveform-bar.speaking:nth-child(10), .waveform-bar.speaking:nth-child(23) { --max-height: 76px; }
        .waveform-bar.speaking:nth-child(11), .waveform-bar.speaking:nth-child(22) { --max-height: 78px; }
        .waveform-bar.speaking:nth-child(12), .waveform-bar.speaking:nth-child(21) { --max-height: 80px; }
        .waveform-bar.speaking:nth-child(13), .waveform-bar.speaking:nth-child(20) { --max-height: 78px; }
        .waveform-bar.speaking:nth-child(14), .waveform-bar.speaking:nth-child(19) { --max-height: 75px; }
        .waveform-bar.speaking:nth-child(15), .waveform-bar.speaking:nth-child(18) { --max-height: 72px; }
        .waveform-bar.speaking:nth-child(16), .waveform-bar.speaking:nth-child(17) { --max-height: 70px; }
        
        @keyframes waveform-active {
          0% { height: 10px; }
          100% { height: var(--max-height, 60px); }
        }
      `}</style>
    </div>
  );
}
